/**
 * Wishlist Feature - mdlondon Design System
 * Handles wishlist add/remove, localStorage management, and UI updates
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'mdlondon_wishlist';
    this.wishlist = this.loadWishlist();
    this.init();
  }

  init() {
    this.updateAllButtons();
    this.setupEventListeners();
    this.updateWishlistCount();
  }

  setupEventListeners() {
    // Wishlist button clicks
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.wishlist-button, .product__wishlist-button');
      if (button) {
        e.preventDefault();
        const productId = button.dataset.productId;
        const productData = {
          id: productId,
          variantId: button.dataset.variantId || productId,
          title: button.dataset.productTitle,
          handle: button.dataset.productHandle,
          price: button.dataset.productPrice,
          image: button.dataset.productImage,
          url: button.dataset.productUrl
        };
        this.toggleWishlist(productId, productData, button);
      }
    });

    // Remove from wishlist page
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.wishlist-item__remove');
      if (removeBtn) {
        e.preventDefault();
        const productId = removeBtn.dataset.productId;
        this.removeFromWishlist(productId);
        this.refreshWishlistPage();
      }
    });

    // Clear wishlist
    document.addEventListener('click', (e) => {
      if (e.target.matches('.wishlist-actions__clear')) {
        e.preventDefault();
        if (confirm('Are you sure you want to clear your wishlist?')) {
          this.clearWishlist();
          this.refreshWishlistPage();
        }
      }
    });

    // Add individual item to cart
    document.addEventListener('click', (e) => {
      const addBtn = e.target.closest('.wishlist-item__add-to-cart');
      if (addBtn) {
        e.preventDefault();
        const productId = addBtn.dataset.productId;
        const variantId = addBtn.dataset.variantId;
        this.addToCart(variantId || productId, addBtn);
      }
    });

    // Add all items to cart
    document.addEventListener('click', (e) => {
      if (e.target.matches('.wishlist-actions__add-all')) {
        e.preventDefault();
        this.addAllToCart();
      }
    });
  }

  toggleWishlist(productId, productData, button) {
    if (this.isInWishlist(productId)) {
      this.removeFromWishlist(productId);
      this.showToast(productData.title + ' removed from wishlist');
    } else {
      this.addToWishlist(productId, productData);
      this.showToast(productData.title + ' added to wishlist');
    }

    if (button) {
      this.updateButton(button, productId);
    }

    this.updateAllButtons();
    this.updateWishlistCount();
  }

  addToWishlist(productId, productData) {
    this.wishlist[productId] = {
      ...productData,
      addedAt: new Date().toISOString()
    };
    this.saveWishlist();

    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('wishlist:added', {
      detail: { productId, productData }
    }));
  }

  removeFromWishlist(productId) {
    delete this.wishlist[productId];
    this.saveWishlist();

    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('wishlist:removed', {
      detail: { productId }
    }));
  }

  clearWishlist() {
    this.wishlist = {};
    this.saveWishlist();
    this.updateAllButtons();
    this.updateWishlistCount();
    this.showToast('Wishlist cleared');
  }

  isInWishlist(productId) {
    return this.wishlist.hasOwnProperty(productId);
  }

  getWishlistItems() {
    return Object.values(this.wishlist);
  }

  getWishlistCount() {
    return Object.keys(this.wishlist).length;
  }

  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error loading wishlist:', error);
      return {};
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  updateButton(button, productId) {
    const isInWishlist = this.isInWishlist(productId);
    button.classList.toggle('wishlist-button--active', isInWishlist);
    button.setAttribute('aria-pressed', isInWishlist);
    // Note: Text visibility is now handled by CSS based on aria-pressed state
    // See product-card-actions.liquid for the CSS rules that show/hide +Wishlist/-Wishlist
  }

  updateAllButtons() {
    document.querySelectorAll('.wishlist-button, .product__wishlist-button').forEach(button => {
      const productId = button.dataset.productId;
      if (productId) {
        this.updateButton(button, productId);
      }
    });
  }

  updateWishlistCount() {
    const count = this.getWishlistCount();

    // Update count text in all badges
    document.querySelectorAll('.wishlist-count').forEach(badge => {
      badge.textContent = count > 0 ? count : '';
    });

    // Toggle visibility on bubble container
    document.querySelectorAll('.wishlist-bubble').forEach(bubble => {
      bubble.classList.toggle('wishlist-bubble--hidden', count === 0);
    });

    // Update page count
    const pageCount = document.querySelector('.wishlist-page__count');
    if (pageCount) {
      pageCount.textContent = count + ' item' + (count !== 1 ? 's' : '');
    }
  }

  refreshWishlistPage() {
    const grid = document.querySelector('.wishlist-grid');
    const emptyState = document.querySelector('.wishlist-empty');

    if (!grid) return;

    const items = this.getWishlistItems();

    if (items.length === 0) {
      if (grid) grid.style.display = 'none';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }

    if (grid) grid.style.display = 'grid';
    if (emptyState) emptyState.style.display = 'none';

    grid.innerHTML = items.map(item => this.renderWishlistItem(item)).join('');
    this.updateWishlistCount();
  }

  renderWishlistItem(item) {
    const variantId = item.variantId || item.id;
    return '<div class="wishlist-item">' +
      '<a href="' + item.url + '" class="wishlist-item__link">' +
        '<div class="wishlist-item__image">' +
          '<img src="' + item.image + '" alt="' + item.title + '" loading="lazy">' +
          '<button class="wishlist-item__remove" data-product-id="' + item.id + '" aria-label="Remove from wishlist">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
          '</button>' +
        '</div>' +
      '</a>' +
      '<div class="wishlist-item__details">' +
        '<h3 class="wishlist-item__title">' +
          '<a href="' + item.url + '">' + item.title + '</a>' +
        '</h3>' +
        '<p class="wishlist-item__price">' + this.formatMoney(item.price) + '</p>' +
        '<button class="wishlist-item__add-to-cart button" data-product-id="' + item.id + '" data-variant-id="' + variantId + '">Add to Cart</button>' +
      '</div>' +
    '</div>';
  }

  showToast(message, type = 'success') {
    const existingToast = document.querySelector('.wishlist-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = 'wishlist-toast wishlist-toast--' + type;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  formatMoney(price) {
    // Handle string prices (convert to number first)
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) {
      return price; // Return original if not a valid number
    }
    const amount = (numPrice / 100).toFixed(2);
    return '£' + amount;
  }

  /**
   * Dispatch Horizon theme cart:update event to update cart icon and open drawer
   * This matches the event structure expected by cart-icon.js and cart-drawer.js
   */
  async dispatchCartUpdate() {
    try {
      // Fetch current cart to get accurate item count
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Dispatch cart:update event with proper structure for Horizon theme
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'wishlist',
          data: {
            itemCount: cart.item_count,
            source: 'wishlist'
          }
        }
      }));
    } catch (error) {
      console.error('Failed to dispatch cart update:', error);
    }
  }

  async addToCart(variantId, button) {
    if (!variantId) {
      this.showToast('Unable to add to cart', 'error');
      return;
    }

    const originalText = button ? button.textContent : '';
    if (button) {
      button.textContent = 'Adding...';
      button.disabled = true;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        this.showToast('Added to cart');
        if (button) {
          button.textContent = 'Added!';
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 2000);
        }
        // Trigger Horizon theme cart update event for cart icon + drawer
        this.dispatchCartUpdate();
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showToast('Failed to add to cart', 'error');
      if (button) {
        button.textContent = originalText;
        button.disabled = false;
      }
    }
  }

  async addAllToCart() {
    const items = this.getWishlistItems();
    if (items.length === 0) {
      this.showToast('Your wishlist is empty', 'error');
      return;
    }

    const addAllBtn = document.querySelector('.wishlist-actions__add-all');
    if (addAllBtn) {
      addAllBtn.textContent = 'Adding...';
      addAllBtn.disabled = true;
    }

    let successCount = 0;
    let failCount = 0;

    for (const item of items) {
      try {
        const variantId = item.variantId || item.id;
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });

        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        failCount++;
      }
    }

    if (addAllBtn) {
      addAllBtn.textContent = 'Add All to Cart';
      addAllBtn.disabled = false;
    }

    if (successCount > 0) {
      this.showToast(successCount + ' item' + (successCount !== 1 ? 's' : '') + ' added to cart');
      // Trigger Horizon theme cart update event for cart icon + drawer
      this.dispatchCartUpdate();
    }
    if (failCount > 0) {
      this.showToast(failCount + ' item' + (failCount !== 1 ? 's' : '') + ' failed to add', 'error');
    }
  }
}

// Initialize wishlist manager (with guard to prevent double initialization)
if (!window.WishlistManager) {
  let wishlistManager;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!window.WishlistManager) {
        wishlistManager = new WishlistManager();
        window.WishlistManager = wishlistManager;
      }
    });
  } else {
    wishlistManager = new WishlistManager();
    window.WishlistManager = wishlistManager;
  }
}
